/* Compacte foto-upload knop met caption-flow.
 * Flow:
 *  1. Klik → opent camera/gallerij (mobiel gebruikt native camera via `capture`).
 *  2. Na file-keuze: preview + korte-toelichting-veld ("Sofia's eerste tekening!")
 *     + Uploaden/Annuleren.
 *  3. Upload naar /api/photos met kind_ids, optioneel daglog_id + caption.
 *  4. Toont thumbnail-strip van reeds geüploade foto's binnen deze sessie.
 * Consent-fouten worden nette toasts met kind-namen.
 */
import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Camera, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CAPTION_MAX = 280;

/**
 * Props:
 * - kindIds: string[] (verplicht; consent moet voor ALLEN true zijn)
 * - daglogId?: string (optioneel; koppelt de foto aan een daglog-event)
 * - onUploaded?: (photoDoc) => void
 * - label?: string (button label; default "Foto")
 */
export default function PhotoUploadButton({ kindIds, daglogId, onUploaded, label = "Foto", variant = "outline", size = "sm" }) {
  const inputRef = useRef(null);
  const [pending, setPending] = useState(null); // {file, previewUrl}
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState([]); // [{id, url, blobUrl, caption}]

  const openPicker = () => inputRef.current?.click();

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset zodat dezelfde file re-selecteerbaar is
    if (!file) return;
    if (!kindIds || kindIds.length === 0) {
      toast.error("Geen kind geselecteerd voor deze foto");
      return;
    }
    setPending({ file, previewUrl: URL.createObjectURL(file) });
    setCaption("");
  };

  const cancelPending = () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
    setCaption("");
  };

  const upload = async () => {
    if (!pending) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", pending.file);
    fd.append("kind_ids", kindIds.join(","));
    if (daglogId) fd.append("daglog_id", daglogId);
    const trimmed = (caption || "").trim();
    if (trimmed) fd.append("caption", trimmed.slice(0, CAPTION_MAX));
    try {
      const { data } = await api.post("/photos", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const blobUrl = pending.previewUrl;
      const t = { id: data.id, url: data.url, blobUrl, caption: trimmed };
      setThumbs(cur => [...cur, t]);
      toast.success(trimmed ? "Foto met toelichting geüpload" : "Foto geüpload");
      onUploaded?.(data);
      // Verlaat dialoog maar houd de blob-url in de thumb — daarom NIET revoke hier.
      setPending(null);
      setCaption("");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403 && detail?.code === "consent_missing") {
        const namen = (detail.kinderen || []).map(k => k.naam).join(", ");
        toast.error(`Toestemming ontbreekt: ${namen || "één of meer kinderen"}`);
      } else if (status === 413) {
        toast.error("Foto is te groot (max 12MB)");
      } else if (status === 415) {
        toast.error("Bestandstype niet toegestaan");
      } else {
        toast.error(formatApiError(detail) || "Upload mislukt");
      }
    } finally {
      setBusy(false);
    }
  };

  const removeThumb = async (t) => {
    try {
      await api.delete(`/photos/${t.id}`);
      setThumbs(cur => cur.filter(x => x.id !== t.id));
      URL.revokeObjectURL(t.blobUrl);
      toast.success("Foto verwijderd");
    } catch (err) {
      toast.error("Verwijderen mislukt");
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="photo-upload-button">
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={openPicker}
        disabled={busy}
        data-testid="photo-upload-btn"
      >
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />}
        {busy ? "Uploaden…" : label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFile}
        data-testid="photo-upload-input"
      />
      {thumbs.length > 0 && (
        <div className="flex items-center gap-1.5" data-testid="photo-upload-thumbs">
          {thumbs.map(t => (
            <div key={t.id} className="relative group">
              <img
                src={t.blobUrl}
                alt={t.caption || "foto"}
                title={t.caption || undefined}
                className="h-10 w-10 rounded-md object-cover border border-neutral-200"
              />
              <button
                type="button"
                onClick={() => removeThumb(t)}
                className="absolute -top-1.5 -right-1.5 bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition"
                title="Verwijderen"
                data-testid={`photo-thumb-remove-${t.id}`}
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Preview + caption dialoog — verschijnt zodra een file is gekozen */}
      <Dialog open={!!pending} onOpenChange={(v) => { if (!v && !busy) cancelPending(); }}>
        <DialogContent className="max-w-sm bg-white" data-testid="photo-caption-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-4 w-4 text-[color:var(--terracotta)]" />
              Foto met korte toelichting
            </DialogTitle>
            <DialogDescription>
              Voeg optioneel een zin toe die de ouder ziet bij de foto (bijv. "Sofia's eerste tekening!").
            </DialogDescription>
          </DialogHeader>
          {pending && (
            <div className="space-y-3">
              <img
                src={pending.previewUrl}
                alt="Voorbeeld"
                className="w-full max-h-56 object-cover rounded-md border border-neutral-200"
              />
              <div>
                <Textarea
                  value={caption}
                  onChange={(e) => setCaption(e.target.value.slice(0, CAPTION_MAX))}
                  placeholder="Korte toelichting (optioneel)"
                  rows={2}
                  data-testid="photo-caption-input"
                  disabled={busy}
                />
                <div className="text-[10px] text-neutral-400 text-right mt-1">
                  {caption.length}/{CAPTION_MAX}
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={cancelPending}
              disabled={busy}
              data-testid="photo-caption-cancel"
            >
              Annuleren
            </Button>
            <Button
              onClick={upload}
              disabled={busy}
              data-testid="photo-caption-upload"
              className="bg-[color:var(--sage)] hover:bg-[color:var(--sage)]/90 text-white"
            >
              {busy ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Uploaden…</>
              ) : (
                "Uploaden"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
